import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Play, Pause, Square, Save, Trash2, MapPin, 
  Clock, TrendingUp, Navigation, Loader2, Share2,
  Download, Mail, MessageCircle, Copy, Check, X
} from 'lucide-react';
import { toast } from 'sonner';
import { MapContainer, TileLayer, Polyline, Marker, useMap } from 'react-leaflet';
import DynamicTileLayer from '@/components/DynamicTileLayer';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Custom marker icon
const currentLocationIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to center map on position
function CenterOnPosition({ position }) {
  const map = useMap();
  
  useEffect(() => {
    if (position) {
      map.setView(position, map.getZoom());
    }
  }, [position, map]);
  
  return null;
}

// Haversine distance calculation
function calculateDistance(lat1, lon1, lat2, lon2) {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
}

// Format time
function formatTime(seconds) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default function TrackRecorder({ onTrackSaved }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [recordedPath, setRecordedPath] = useState([]);
  const [distance, setDistance] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [trackName, setTrackName] = useState('');
  const [trackDescription, setTrackDescription] = useState('');
  const [savingTrack, setSavingTrack] = useState(false);
  const [lastSavedTrack, setLastSavedTrack] = useState(null);
  const [copied, setCopied] = useState(false);
  
  const watchIdRef = useRef(null);
  const timerRef = useRef(null);
  const lastPositionRef = useRef(null);

  // Start GPS tracking
  const startGPS = useCallback(() => {
    if (!navigator.geolocation) {
      setGpsError('GPS no disponible en este dispositivo');
      return;
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        const { latitude, longitude, accuracy, altitude, speed } = position.coords;
        const timestamp = position.timestamp;
        
        const newPoint = {
          lat: latitude,
          lon: longitude,
          accuracy,
          altitude: altitude || null,
          speed: speed || null,
          timestamp
        };
        
        setCurrentPosition([latitude, longitude]);
        setGpsError(null);
        
        // Only add to path if recording and not paused
        if (isRecording && !isPaused) {
          // Calculate distance from last point
          if (lastPositionRef.current) {
            const dist = calculateDistance(
              lastPositionRef.current.lat,
              lastPositionRef.current.lon,
              latitude,
              longitude
            );
            // Only add point if moved more than 5 meters (avoid GPS jitter)
            if (dist > 0.005) {
              setDistance(prev => prev + dist);
              setRecordedPath(prev => [...prev, newPoint]);
              lastPositionRef.current = newPoint;
            }
          } else {
            setRecordedPath(prev => [...prev, newPoint]);
            lastPositionRef.current = newPoint;
          }
        }
      },
      (error) => {
        console.error('GPS error:', error);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGpsError('Permiso de ubicación denegado');
            break;
          case error.POSITION_UNAVAILABLE:
            setGpsError('Ubicación no disponible');
            break;
          case error.TIMEOUT:
            setGpsError('Tiempo de espera agotado');
            break;
          default:
            setGpsError('Error de GPS desconocido');
        }
      },
      {
        enableHighAccuracy: true,
        maximumAge: 3000,
        timeout: 10000
      }
    );
  }, [isRecording, isPaused]);

  // Stop GPS tracking
  const stopGPS = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopGPS();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [stopGPS]);

  // Start recording
  const handleStartRecording = () => {
    setIsRecording(true);
    setIsPaused(false);
    setRecordedPath([]);
    setDistance(0);
    setElapsedTime(0);
    setStartTime(new Date());
    lastPositionRef.current = null;
    
    startGPS();
    
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    
    toast.success('Grabación iniciada');
  };

  // Pause recording
  const handlePauseRecording = () => {
    setIsPaused(true);
    if (timerRef.current) clearInterval(timerRef.current);
    toast.info('Grabación pausada');
  };

  // Resume recording
  const handleResumeRecording = () => {
    setIsPaused(false);
    timerRef.current = setInterval(() => {
      setElapsedTime(prev => prev + 1);
    }, 1000);
    toast.success('Grabación reanudada');
  };

  // Stop recording
  const handleStopRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    if (timerRef.current) clearInterval(timerRef.current);
    stopGPS();
    
    if (recordedPath.length > 1) {
      setShowSaveDialog(true);
    } else {
      toast.warning('No hay suficientes puntos grabados');
      setRecordedPath([]);
      setDistance(0);
      setElapsedTime(0);
    }
  };

  // Discard recording
  const handleDiscardRecording = () => {
    setIsRecording(false);
    setIsPaused(false);
    if (timerRef.current) clearInterval(timerRef.current);
    stopGPS();
    setRecordedPath([]);
    setDistance(0);
    setElapsedTime(0);
    toast.info('Grabación descartada');
  };

  // Save track
  const handleSaveTrack = async () => {
    if (recordedPath.length < 2) {
      toast.error('No hay suficientes puntos para guardar');
      return;
    }
    
    setSavingTrack(true);
    try {
      const token = localStorage.getItem('token');
      const savedTrackName = trackName || `Track ${new Date().toLocaleDateString('es-ES')}`;
      
      // Create GPX content
      const gpxContent = generateGPX(recordedPath, savedTrackName, trackDescription);
      const gpxBlob = new Blob([gpxContent], { type: 'application/gpx+xml' });
      const gpxFile = new File([gpxBlob], `${savedTrackName.replace(/[^a-zA-Z0-9]/g, '_')}.gpx`, { 
        type: 'application/gpx+xml' 
      });
      
      const formData = new FormData();
      formData.append('name', savedTrackName);
      formData.append('date', new Date().toISOString().split('T')[0]);
      formData.append('description', trackDescription || '');
      formData.append('gpx_file', gpxFile);
      formData.append('source', 'recorded');
      formData.append('distance_km', distance.toFixed(2));
      formData.append('duration_minutes', Math.round(elapsedTime / 60));
      
      const response = await axios.post(`${API}/user/tracks`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Track guardado correctamente');
      
      setLastSavedTrack({
        id: response.data.track?.id,
        name: savedTrackName,
        distance_km: distance,
        duration_seconds: elapsedTime,
        gpx_file_url: response.data.track?.gpx_file_url
      });
      
      setShowSaveDialog(false);
      setShowShareDialog(true);
      setRecordedPath([]);
      setDistance(0);
      setElapsedTime(0);
      setTrackName('');
      setTrackDescription('');
      
      if (onTrackSaved) onTrackSaved();
      
    } catch (error) {
      console.error('Error saving track:', error);
      toast.error(error.response?.data?.detail || 'Error al guardar el track');
    } finally {
      setSavingTrack(false);
    }
  };

  // Generate GPX content
  const generateGPX = (points, name, description) => {
    const gpxHeader = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Strategos Overland"
  xmlns="http://www.topografix.com/GPX/1/1">
  <metadata>
    <name>${name}</name>
    <desc>${description || ''}</desc>
    <time>${new Date().toISOString()}</time>
  </metadata>
  <trk>
    <name>${name}</name>
    <trkseg>`;
    
    const gpxPoints = points.map(p => {
      let trkpt = `      <trkpt lat="${p.lat}" lon="${p.lon}">`;
      if (p.altitude) trkpt += `\n        <ele>${p.altitude}</ele>`;
      if (p.timestamp) trkpt += `\n        <time>${new Date(p.timestamp).toISOString()}</time>`;
      trkpt += `\n      </trkpt>`;
      return trkpt;
    }).join('\n');
    
    const gpxFooter = `
    </trkseg>
  </trk>
</gpx>`;
    
    return gpxHeader + '\n' + gpxPoints + gpxFooter;
  };

  // Share functions
  const handleDownloadGPX = () => {
    if (!lastSavedTrack?.gpx_file_url) {
      toast.error('GPX no disponible');
      return;
    }
    window.open(lastSavedTrack.gpx_file_url, '_blank');
    toast.success('Descargando GPX...');
  };

  const handleCopyLink = async () => {
    if (!lastSavedTrack?.gpx_file_url) return;
    try {
      await navigator.clipboard.writeText(lastSavedTrack.gpx_file_url);
      setCopied(true);
      toast.success('Enlace copiado');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  const handleShareWhatsApp = () => {
    if (!lastSavedTrack) return;
    const text = `🏍️ Mi track "${lastSavedTrack.name}"\n📏 ${lastSavedTrack.distance_km.toFixed(1)} km\n⏱️ ${formatTime(lastSavedTrack.duration_seconds)}\n\n${lastSavedTrack.gpx_file_url || ''}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  const handleShareEmail = () => {
    if (!lastSavedTrack) return;
    const subject = `Track: ${lastSavedTrack.name}`;
    const body = `¡Hola!\n\nTe comparto mi track "${lastSavedTrack.name}":\n\n📏 Distancia: ${lastSavedTrack.distance_km.toFixed(1)} km\n⏱️ Duración: ${formatTime(lastSavedTrack.duration_seconds)}\n\nDescargar GPX: ${lastSavedTrack.gpx_file_url || 'No disponible'}\n\n¡Un saludo!`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`, '_blank');
  };

  const handleNativeShare = async () => {
    if (!navigator.share || !lastSavedTrack) return;
    try {
      await navigator.share({
        title: `Track: ${lastSavedTrack.name}`,
        text: `Mi track "${lastSavedTrack.name}" - ${lastSavedTrack.distance_km.toFixed(1)} km`,
        url: lastSavedTrack.gpx_file_url
      });
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Error al compartir');
      }
    }
  };

  const recordedPathForMap = recordedPath.map(p => [p.lat, p.lon]);

  return (
    <>
      <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Map Preview */}
            <div className="flex-1 h-48 md:h-auto rounded-lg overflow-hidden border">
              {currentPosition ? (
                <MapContainer
                  center={currentPosition}
                  zoom={15}
                  style={{ height: '100%', width: '100%', minHeight: '200px' }}
                >
                  <DynamicTileLayer />
                  {recordedPathForMap.length > 1 && (
                    <Polyline
                      positions={recordedPathForMap}
                      color="#22c55e"
                      weight={4}
                    />
                  )}
                  <Marker position={currentPosition} icon={currentLocationIcon} />
                  <CenterOnPosition position={currentPosition} />
                </MapContainer>
              ) : (
                <div className="h-full flex items-center justify-center bg-gray-100 text-gray-500">
                  <div className="text-center">
                    <Navigation className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Inicia la grabación para ver el mapa</p>
                  </div>
                </div>
              )}
            </div>
            
            {/* Controls */}
            <div className="md:w-64 space-y-4">
              <div className="text-center">
                <h3 className="font-semibold text-lg text-gray-800 mb-1">
                  {isRecording ? (isPaused ? 'Pausado' : 'Grabando...') : 'Grabar Track'}
                </h3>
                {gpsError && (
                  <p className="text-xs text-red-500">{gpsError}</p>
                )}
              </div>
              
              {/* Stats */}
              {isRecording && (
                <div className="grid grid-cols-2 gap-2 text-center">
                  <div className="bg-white rounded-lg p-2">
                    <Clock className="w-4 h-4 mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold">{formatTime(elapsedTime)}</p>
                    <p className="text-xs text-gray-500">Tiempo</p>
                  </div>
                  <div className="bg-white rounded-lg p-2">
                    <TrendingUp className="w-4 h-4 mx-auto text-green-600 mb-1" />
                    <p className="text-lg font-bold">{distance.toFixed(2)}</p>
                    <p className="text-xs text-gray-500">Km</p>
                  </div>
                </div>
              )}
              
              {/* Buttons */}
              <div className="flex flex-col gap-2">
                {!isRecording ? (
                  <Button
                    onClick={handleStartRecording}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="start-track-recording-btn"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Iniciar Grabación
                  </Button>
                ) : (
                  <>
                    {isPaused ? (
                      <Button
                        onClick={handleResumeRecording}
                        className="w-full bg-green-600 hover:bg-green-700"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Reanudar
                      </Button>
                    ) : (
                      <Button
                        onClick={handlePauseRecording}
                        variant="outline"
                        className="w-full border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        Pausar
                      </Button>
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={handleStopRecording}
                        className="flex-1 bg-blue-600 hover:bg-blue-700"
                        data-testid="stop-track-recording-btn"
                      >
                        <Square className="w-4 h-4 mr-2" />
                        Parar y Guardar
                      </Button>
                      <Button
                        onClick={handleDiscardRecording}
                        variant="outline"
                        className="text-red-500 border-red-300 hover:bg-red-50"
                        title="Descartar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </>
                )}
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                {isRecording 
                  ? `${recordedPath.length} puntos grabados`
                  : 'Graba tu ruta mientras conduces'
                }
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Save Dialog */}
      <Dialog open={showSaveDialog} onOpenChange={setShowSaveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-green-600" />
              Guardar Track
            </DialogTitle>
            <DialogDescription>
              Has grabado {distance.toFixed(2)} km en {formatTime(elapsedTime)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="track_name">Nombre del track *</Label>
              <Input
                id="track_name"
                value={trackName}
                onChange={(e) => setTrackName(e.target.value)}
                placeholder={`Track ${new Date().toLocaleDateString('es-ES')}`}
                className="mt-1"
                autoFocus
              />
            </div>
            
            <div>
              <Label htmlFor="track_desc">Descripción (opcional)</Label>
              <Input
                id="track_desc"
                value={trackDescription}
                onChange={(e) => setTrackDescription(e.target.value)}
                placeholder="Notas sobre la ruta..."
                className="mt-1"
              />
            </div>
            
            <div className="flex gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowSaveDialog(false);
                  setRecordedPath([]);
                  setDistance(0);
                  setElapsedTime(0);
                }}
                className="flex-1"
              >
                Descartar
              </Button>
              <Button
                onClick={handleSaveTrack}
                disabled={savingTrack}
                className="flex-1 bg-green-600 hover:bg-green-700"
              >
                {savingTrack ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Guardar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-blue-600" />
              Track Guardado
            </DialogTitle>
            <DialogDescription>
              "{lastSavedTrack?.name}" - {lastSavedTrack?.distance_km?.toFixed(1)} km
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            <p className="text-sm text-gray-600 text-center">
              ¿Cómo quieres compartir o guardar tu track?
            </p>
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={handleDownloadGPX}
                className="flex flex-col items-center py-4 h-auto"
              >
                <Download className="w-5 h-5 mb-1 text-green-600" />
                <span className="text-xs">Descargar GPX</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleCopyLink}
                className="flex flex-col items-center py-4 h-auto"
              >
                {copied ? (
                  <Check className="w-5 h-5 mb-1 text-green-600" />
                ) : (
                  <Copy className="w-5 h-5 mb-1 text-gray-600" />
                )}
                <span className="text-xs">{copied ? 'Copiado!' : 'Copiar enlace'}</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleShareWhatsApp}
                className="flex flex-col items-center py-4 h-auto"
              >
                <MessageCircle className="w-5 h-5 mb-1 text-green-500" />
                <span className="text-xs">WhatsApp</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleShareEmail}
                className="flex flex-col items-center py-4 h-auto"
              >
                <Mail className="w-5 h-5 mb-1 text-blue-600" />
                <span className="text-xs">Email</span>
              </Button>
            </div>
            
            {navigator.share && (
              <Button
                onClick={handleNativeShare}
                variant="outline"
                className="w-full"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Más opciones...
              </Button>
            )}
            
            <Button
              onClick={() => setShowShareDialog(false)}
              className="w-full mt-2"
            >
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
